import { Button, Box, Typography } from '@mui/material';
import { useTranslate, useRecordContext } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { GrayDateInputWithUpperLabel } from './GrayDateInputWithUpperLabel';

type PlannedRenewalDateInputFieldProps = {
  label?: string;
  helperText?: string;
  clearButtonPlacement?: 'inline' | 'below';
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  isRequired?: boolean;
};

const ClearButton = (props: { onClick: () => void; tabIndex?: number }) => {

  const { onClick, tabIndex } = props;
  const translate = useTranslate();
  return (
    <Button sx={{
      p: 0,
      m: 0,
      minWidth: 0,
      minHeight: 0,
      textTransform: 'none',
      textDecoration: 'underline',
      justifyContent: 'flex-start',
      fontWeight: 'normal',
      fontSize: 'inherit',
      lineHeight: 'inherit',
      '&:hover': {
        textDecoration: 'underline',
        backgroundColor: 'transparent',
      },
    }} onClick={onClick} tabIndex={tabIndex}>
      {translate('general.text.clear')}
    </Button>
  );

};

export const PlannedRenewalDateInputField = (props: PlannedRenewalDateInputFieldProps) => {

  const {
    label = 'resources.security-inventory-items.fields.planned_inspection_date',
    helperText,
    clearButtonPlacement = 'inline',
    fullWidth = false,
    size = 'medium',
    isRequired,
  } = props;

  const translate = useTranslate();
  const record = useRecordContext();
  const { setValue } = useFormContext();
  const plannedRenewalDate = useWatch({ name: 'plannedRenewalDate' });

  const currentPlannedRenewalDate = plannedRenewalDate !== undefined
    ? plannedRenewalDate
    : record?.plannedRenewalDate;
  const showPlannedRenewalDateField = Boolean(currentPlannedRenewalDate);

  const clearPlannedRenewalDate = () => {

    setValue('plannedRenewalDate', null, { shouldDirty: true, shouldValidate: true });

  };

  return (
    <Box sx={{
      position: clearButtonPlacement === 'inline' ? 'relative' : undefined,
      maxWidth: fullWidth ? undefined : 160,
      width: fullWidth ? '100%' : undefined,
      display: clearButtonPlacement === 'below' ? 'flex' : undefined,
      flexDirection: clearButtonPlacement === 'below' ? 'column' : undefined,
      alignItems: clearButtonPlacement === 'below' ? 'flex-start' : undefined,
    }}>
      <GrayDateInputWithUpperLabel
        source="plannedRenewalDate"
        label={label}
        helperText={false}
        size={size}
        sx={{ width: fullWidth ? '100%' : 160, mt: 0, mb: 0 }}
        isRequired={isRequired}
      />
      {helperText && (
        <Typography
          color="text.secondary"
          sx={{
            fontSize: 10,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            mt: 0.5,
          }}
        >
          {translate(helperText)}
        </Typography>
      )}
      {clearButtonPlacement === 'below' && (
        <Box
          sx={{
            mt: 0.5,
            minHeight: '2.4em',
            visibility: showPlannedRenewalDateField ? 'visible' : 'hidden',
          }}
          aria-hidden={!showPlannedRenewalDateField}
        >
          <ClearButton
            onClick={clearPlannedRenewalDate}
            tabIndex={showPlannedRenewalDateField ? 0 : -1}
          />
        </Box>
      )}
      {clearButtonPlacement === 'inline' && showPlannedRenewalDateField && (
        <Box sx={{
          position: 'absolute',
          left: '100%',
          bottom: 0,
          ml: 1,
          height: size === 'small' ? 40 : 56,
          display: 'flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
        }}>
          <ClearButton onClick={clearPlannedRenewalDate} />
        </Box>
      )}
    </Box>
  );

};
